import React, {useEffect, useState} from "react";
import {Form, Button, Message} from "semantic-ui-react";
import {useForm} from "react-hook-form";

import {PHONE_NUMBER} from "../../../constants/company";
import {
  normalizeCustomerName,
  validateCustomerName,
  validateEmail,
  validateOptionalSecondaryEmail,
  validatePassword,
} from "../../../utils/validation";
import {
  CUSTOMER_DELIVERY_PRICE_MAX,
  CUSTOMER_DELIVERY_PRICE_MIN,
  resolveCustomerDeliveryPrice,
} from "../../../utils/deliveryPrice";
import {AdminModel} from "../../../models/AdminModel";

interface FieldTypes {
  givenName: string;
  familyName: string;
  deliveryPrice: string;
  email: string;
  secondaryEmail: string;
  password: string;
  confirmPassword: string;
  isAdmin: boolean;
  excludeFromInvoiceReminders: boolean; // exclude specific customers from invoice reminders
}

export const EditUserForm: React.FC<any> = ({user, onClose}) => {
  const [hasServerError, setServerError] = useState<boolean>(false);
  const initialDeliveryPrice = resolveCustomerDeliveryPrice(user);
  const {
    register,
    handleSubmit,
    formState: {errors, isValid, isSubmitting, dirtyFields},
    watch,
    setValue,
    trigger,
  } = useForm<FieldTypes>({
    defaultValues: {
      givenName: user.givenName || "",
      familyName: user.familyName || "",
      deliveryPrice:
        initialDeliveryPrice !== null ? initialDeliveryPrice.toFixed(2) : "",
      email: user.email,
      secondaryEmail: user.secondaryEmail || "",
      isAdmin: user.role === "admin",
      excludeFromInvoiceReminders: user.excludeFromInvoiceReminders || false,
    }
  });

  const watchIsAdmin = watch("isAdmin", user.role === "admin");
  const watchExcludeFromInvoiceReminders = watch(
    "excludeFromInvoiceReminders",
    user.excludeFromInvoiceReminders || false,
  );

  const onSubmit = async ({
    givenName,
    familyName,
    deliveryPrice,
    email,
    secondaryEmail,
    password,
    isAdmin,
    excludeFromInvoiceReminders,
  }: FieldTypes) => {
    const normalizedPrice = String(deliveryPrice || "").trim();
    const parsedPrice = Number(normalizedPrice);
    // Only send a real price change; blank or unchanged values must not claim website ownership.
    const priceChanged =
      Boolean(dirtyFields.deliveryPrice) &&
      normalizedPrice !== "" &&
      (initialDeliveryPrice === null || parsedPrice !== initialDeliveryPrice);
    const updatedUser = await AdminModel.putUser({
      _id: user._id,
      givenName: normalizeCustomerName(givenName),
      familyName: normalizeCustomerName(familyName),
      email,
      ...(dirtyFields.secondaryEmail ? {secondaryEmail: secondaryEmail.trim()} : {}),
      password,
      isAdmin,
      excludeFromInvoiceReminders,
      ...(priceChanged ? {deliveryPrice: parsedPrice} : {}),
    });
    if (updatedUser) {
      onClose();
    } else {
      setServerError(true);
    }
  };

  const deleteUser = async () => {
    await AdminModel.deleteUser(user._id);
    onClose();
  }

  useEffect(() => {
    register("givenName", {
      required: true,
      validate: (value) => validateCustomerName(value),
    });
    register("familyName", {
      required: true,
      validate: (value) => validateCustomerName(value),
    });
    register("deliveryPrice", {
      validate: (value) => {
        const normalized = String(value || "").trim();
        if (!normalized) return true;
        const parsed = Number(normalized);
        return (
          Number.isFinite(parsed) &&
          parsed >= CUSTOMER_DELIVERY_PRICE_MIN &&
          parsed <= CUSTOMER_DELIVERY_PRICE_MAX
        );
      },
    });
    register("email", {
      validate: (value) => validateEmail(value),
    });
    register("secondaryEmail", {
      validate: (value) =>
        validateOptionalSecondaryEmail(value, watch("email")) ||
        "Please enter a valid secondary email different from the primary email",
    });
    register("password", {
      validate: (value) => {
        // If admin doesn't want to change the user password
        if (!watch("confirmPassword") && !value) {
          return true
        } else {
          return value && validatePassword(value)
        }
      },
    });
    register("confirmPassword", {
      validate: (value) => {
        return value === watch("password")
      },
    });
    trigger([
      "givenName",
      "familyName",
      "deliveryPrice",
      "email",
      "secondaryEmail",
      "password",
      "confirmPassword",
      "isAdmin",
    ]);
  }, []);

  return (
    <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)} error={hasServerError}>
      <Form.Input
        fluid
        label="First Name"
        placeholder="First Name"
        defaultValue={user.givenName || ""}
        name="givenName"
        onChange={(e: any) =>
          setValue("givenName", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        onBlur={(e: any) =>
          setValue("givenName", normalizeCustomerName(e.target.value), {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.givenName
            ? {
              content:
                "First name can only contain letters, spaces, hyphens, and apostrophes, max 50 characters",
              pointing: "above",
            }
            : undefined
        }
      />
      <Form.Input
        fluid
        label="Last Name"
        placeholder="Last Name"
        defaultValue={user.familyName || ""}
        name="familyName"
        onChange={(e: any) =>
          setValue("familyName", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        onBlur={(e: any) =>
          setValue("familyName", normalizeCustomerName(e.target.value), {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.familyName
            ? {
              content:
                "Last name can only contain letters, spaces, hyphens, and apostrophes, max 50 characters",
              pointing: "above",
            }
            : undefined
        }
      />
      <Form.Input
        fluid
        label="Delivery Price ($)"
        placeholder="Leave blank to keep the current price"
        type="number"
        min="0.01"
        max="1000000"
        step="0.01"
        defaultValue={
          initialDeliveryPrice !== null ? initialDeliveryPrice.toFixed(2) : ""
        }
        name="deliveryPrice"
        onChange={(e: any) =>
          setValue("deliveryPrice", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        onBlur={(e: any) =>
          setValue("deliveryPrice", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.deliveryPrice
            ? {
              content: "Please enter a delivery price between $0.01 and $1,000,000",
              pointing: "above",
            }
            : undefined
        }
      />
      <Form.Input
        fluid
        label="Email Address"
        placeholder="Email Address"
        defaultValue={user.email}
        name="email"
        onChange={async (e: any) => {
          setValue("email", e.target.value);
          // only update validation on change if the user corrected their email address
          if (validateEmail(e.target.value)) {
            await trigger("email");
          }
        }}
        onBlur={(e: any) =>
          setValue("email", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.email
            ? {
              content: "Please enter a valid email address",
              pointing: "above",
            }
            : undefined
        }
      />
      <Form.Input
        fluid
        id="secondaryEmail"
        label="Secondary Email (CC notifications only)"
        placeholder="Optional secondary email"
        defaultValue={user.secondaryEmail || ""}
        name="secondaryEmail"
        onChange={async (e: any) => {
          setValue("secondaryEmail", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        onBlur={(e: any) =>
          setValue("secondaryEmail", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.secondaryEmail
            ? {
              content:
                errors.secondaryEmail.message ||
                "Please enter a valid secondary email different from the primary email",
              pointing: "above",
            }
            : undefined
        }
      />
      <Form.Input
        fluid
        autoComplete="new-password"
        label="Password"
        placeholder="Enter a password"
        type="password"
        name="password"
        onChange={async (e: any) => {
          setValue("password", e.target.value);
          // only update validation on change if the user corrected their password
          if (validatePassword(e.target.value) || !e.target.value) {
            await trigger("password");
          }
        }}
        onBlur={(e: any) =>
          setValue("password", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.password
            ? {
              content: "Password must be 6 characters long",
              pointing: "above",
            }
            : undefined
        }
      />
      <Form.Input
        fluid
        autoComplete="new-password"
        name="confirmPassword"
        label="Confirm Password"
        placeholder="Confirm your password"
        type="password"
        onChange={async (e: any) => {
          setValue("confirmPassword", e.target.value);
          // only update validation on change if the user corrected their password
          if (e.target.value === watch("password") || !e.target.value) {
            await trigger("confirmPassword");
          }
        }}
        onBlur={(e: any) =>
          setValue("confirmPassword", e.target.value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        error={
          errors.confirmPassword
            ? {
              content: "Passwords must match",
              pointing: "above",
            }
            : undefined
        }
      />
      <Form.Checkbox
        fluid
        name="isAdmin"
        label="User is an admin"
        defaultChecked={watchIsAdmin}
        checked={watchIsAdmin}
        value={watchIsAdmin ? "admin" : "user"}
        onChange={() => setValue("isAdmin", !watchIsAdmin, {shouldDirty: true})}
      />
      <Form.Checkbox
        fluid
        name="excludeFromInvoiceReminders"
        label="Exclude from invoice reminders"
        defaultChecked={watchExcludeFromInvoiceReminders}
        checked={watchExcludeFromInvoiceReminders}
        onChange={() =>
          setValue(
            "excludeFromInvoiceReminders",
            !watchExcludeFromInvoiceReminders,
            {shouldDirty: true},
          )
        }
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button type="submit" disabled={!isValid || isSubmitting}>
          Submit
        </Button>
        <Button
          type="button"
          color="red"
          onClick={deleteUser}
        >
          Delete User
        </Button>
      </div>
      <Message
        error
        header="Server Error"
        content={`Uh oh! Our computers screwed up again. Please try again later or call us at ${PHONE_NUMBER}.`}
      />
    </Form>
  );
};
